/**
 * Field-class merge for prospect → User prefill (agent epic PR 3, D3 as
 * amended 2026-06-20).
 *
 * Two classes of fields, two rules:
 *
 *   VOLATILE STATS — the dataset ALWAYS wins, even over a non-empty
 *   value. Founder ruling: "the athlete isn't constantly updating their
 *   own stats" — follower counts drift stale on the profile and fresh in
 *   the dataset, so an invite (or a future "refresh stats" action)
 *   overwrites them.
 *
 *   IDENTITY / SELF-AUTHORED — fill-empty-only. A real athlete's own
 *   name, bio, handles, sport, or NCAA-adjacent fields must NEVER be
 *   clobbered by a dataset record. These only fill genuine gaps (new stub
 *   users, sparse profiles).
 *
 * The two lists are exported constants so the classification lives in
 * exactly one testable place, reusable by a later "refresh stats from
 * dataset" action.
 */

import type { FoundryAthlete } from '@app/foundry-athletes';

/** Dataset always wins — overwrites non-empty profile values. */
export const VOLATILE_STAT_FIELDS = [
  'instagramFollowers',
  'tiktokFollowers',
  'twitterFollowers',
  'youtubeFollowers',
  'socialFollowers',
  'socialEngagement',
] as const;

/**
 * Fill-empty-only — never clobber what an athlete wrote themselves.
 *
 * `classYear` is deliberately ABSENT: the real dataset's class year is a
 * recruiting class year ("2026"), while the profile field holds the
 * freshman/sophomore/junior/senior/graduate enum — prefilling one into
 * the other would render nonsense in the profile's select.
 */
export const IDENTITY_FILL_EMPTY_FIELDS = [
  'name',
  'sport',
  'position',
  'school',
  'conference',
  'division',
  'hometown',
  'instagramHandle',
  'tiktokHandle',
  'twitterHandle',
  'youtubeHandle',
] as const;

type MergeableField =
  | (typeof VOLATILE_STAT_FIELDS)[number]
  | (typeof IDENTITY_FILL_EMPTY_FIELDS)[number];

/** "Empty" for merge purposes: null, undefined, or whitespace-only. */
function isEmpty(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
}

/**
 * Build the `user.update` patch that applies a dataset record onto an
 * existing User row per the field-class rules. Returns only the keys that
 * should change — an empty object means nothing to write.
 *
 * @param existing  Current User row values for the mergeable fields.
 * @param dataset   The prospect's snapshotted dataset record.
 * @param opts.isNewUser  A user created BY this invite has only a
 *   placeholder name (email prefix, set by the invite core). Identity
 *   fill-empty would see that placeholder as "non-empty" and keep it, so
 *   for brand-new users the dataset name is applied anyway. Never true
 *   for a user who existed before the invite.
 */
export function buildProspectMergePatch(
  existing: Partial<Record<MergeableField, unknown>>,
  dataset: FoundryAthlete,
  opts: { isNewUser: boolean },
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};

  for (const field of VOLATILE_STAT_FIELDS) {
    const value = dataset[field];
    // Only a present dataset value overwrites — a dataset gap must not
    // null out a stat the athlete does have.
    if (value !== null && value !== undefined && existing[field] !== value) {
      patch[field] = value;
    }
  }

  for (const field of IDENTITY_FILL_EMPTY_FIELDS) {
    let value = dataset[field];
    if (value === null || value === undefined || (typeof value === 'string' && !value.trim())) {
      continue;
    }
    // The dataset stores sport/division as slugs ('basketball', 'ncaa-d1');
    // the profile fields are display strings. Prettify on the way in so a
    // prefilled profile reads like one the athlete typed.
    if ((field === 'sport' || field === 'division') && typeof value === 'string') {
      value = prettifySlug(value);
    }
    const fillable = field === 'name' ? opts.isNewUser || isEmpty(existing[field]) : isEmpty(existing[field]);
    if (fillable && existing[field] !== value) {
      patch[field] = value;
    }
  }

  return patch;
}

/** 'basketball' → 'Basketball'; 'ncaa-d1' → 'NCAA D1'; 'track_field' →
 * 'Track Field'. Local (not imported from the provider package) so this
 * module stays value-import-free of it — it's statically reachable from
 * the client bundle via the router barrel. */
function prettifySlug(slug: string): string {
  const DIVISIONS: Record<string, string> = {
    'ncaa-d1': 'NCAA D1',
    'ncaa-d2': 'NCAA D2',
    'ncaa-d3': 'NCAA D3',
    naia: 'NAIA',
    juco: 'JUCO',
  };
  if (DIVISIONS[slug]) return DIVISIONS[slug];
  return slug
    .split(/[_\s]+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}
